import {
  canAccessLibrary,
  canAccessClientPreview,
  canViewUsers,
  isAdminRole,
  isPmaRole,
  isRetailerRole,
} from '../../../../utils/role-utils';

const CLIENT_VIEW_PATH = '/hac/main';
const EXTERNAL_EXPERIENCES_URL = 'https://www.pre-rrc-info.com/current-experiences';

const CLIENT_VIEW_LINK_TAB = {
  type: 'link',
  href: CLIENT_VIEW_PATH,
  labelKey: 'common-client-view',
  className: 'opossed active',
  rel: 'noreferrer',
};

const CLIENT_PREVIEW_LINK_TAB = {
  type: 'link',
  href: CLIENT_VIEW_PATH,
  labelKey: 'common-client-view',
  className: 'opossed',
  rel: 'noreferrer',
};

const COMMON_EXPERIENCES_TAB = {
  type: 'link',
  href: EXTERNAL_EXPERIENCES_URL,
  labelKey: 'common-experiences',
  target: '_blank',
  rel: 'noreferrer',
  iconClassName: 'icon-link-out',
  linkClassName: 'role-ribbon-link-flush',
};

const withActiveClass = (tab, isActive) => ({
  ...tab,
  className: [tab.className, isActive ? 'active' : ''].filter(Boolean).join(' '),
});

const getAdminMainTabs = ({ clientPreviewActive = false, libraryActive = false, role } = {}) => {
  const showClientPreview = canAccessClientPreview(role);

  return [
    ...(canViewUsers(role)
      ? [withActiveClass({ type: 'route', to: '/admin/main/hac', labelKey: 'common-users' }, !libraryActive && !clientPreviewActive)]
      : []),
    ...(canAccessLibrary(role)
      ? [withActiveClass({ type: 'route', to: '/admin/main/library', labelKey: 'library-title' }, libraryActive)]
      : []),
    ...(showClientPreview ? [withActiveClass(CLIENT_PREVIEW_LINK_TAB, clientPreviewActive)] : []),
    {
      ...COMMON_EXPERIENCES_TAB,
      className: showClientPreview ? COMMON_EXPERIENCES_TAB.className : 'opossed',
    },
  ];
};

const getAdminCatalogTabs = (role) => getAdminMainTabs({ libraryActive: true, role });

const getPmaMainTabs = (activeTab) => [
  withActiveClass({ type: 'route', to: '/pma/calendar', labelKey: 'common-calendar' }, activeTab === 'calendar'),
  withActiveClass({ type: 'route', to: '/pma/main/hac', labelKey: 'common-users' }, ['hac', 'pma', 'retailer'].includes(activeTab)),
  withActiveClass(
    { ...CLIENT_VIEW_LINK_TAB, className: 'opossed' },
    activeTab === 'clientView'
  ),
  COMMON_EXPERIENCES_TAB,
];

const getRetailerMainTabs = ({ clientViewActive = false } = {}) => [
  withActiveClass(
    { type: 'route', to: '/retailer/main/nominated', labelKey: 'common-rrc-members' },
    !clientViewActive
  ),
  withActiveClass(
    { ...CLIENT_VIEW_LINK_TAB, className: 'opossed' },
    clientViewActive
  ),
];

const getHacManagementTabsForRole = (role) => {
  if (isRetailerRole(role)) {
    return getRetailerMainTabs();
  }

  if (isPmaRole(role)) {
    return getPmaMainTabs('hac');
  }

  if (isAdminRole(role)) {
    return getAdminMainTabs({ role });
  }

  return [];
};

const getClientViewTabsForRole = (role) => {
  if (role === 'hac') {
    return [];
  }

  if (isRetailerRole(role)) {
    return getRetailerMainTabs({ clientViewActive: true });
  }

  if (isPmaRole(role)) {
    return getPmaMainTabs('clientView');
  }

  if (isAdminRole(role)) {
    return getAdminMainTabs({ clientPreviewActive: true, role });
  }

  if (canAccessLibrary(role) && canAccessClientPreview(role)) {
    return getAdminMainTabs({ clientPreviewActive: true, role });
  }

  return [];
};

const getRoleRibbonTabs = (role, context = 'hacManagement') => {
  if (context === 'clientView') {
    return getClientViewTabsForRole(role);
  }

  return getHacManagementTabsForRole(role);
};

const getExperienceTabsForRole = (role) => getClientViewTabsForRole(role);

export {
  getHacManagementTabsForRole,
  getClientViewTabsForRole,
  getAdminMainTabs,
  getAdminCatalogTabs,
  getPmaMainTabs,
  getRetailerMainTabs,
  getExperienceTabsForRole,
  getRoleRibbonTabs,
};
